import { useMemo, useState } from "react"
import { EmpreendimentoBlocoCompletoTable, ListaEmpreendimentoBlocoCompletoTable } from "../../../../domain/models/empreendimentoBlocoTable"
import { IncorporacaoImovel } from "../../../../domain/models/incorporacao_imovel"
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from "@mui/material"
import DynamicIcon from "../../../../../../common/components/dynamic_icon"
import { blocoAlteracao } from "../../../../domain/models/empreendimentoBlocosAlteracao"
import { unidadeAlteracao } from "../../../../domain/models/empreendimentoUnidadesAlteracao"
import { EditableCellBloco, EditableCellUnidade } from "./editable_cells"



type Props = {
    blocoTable: EmpreendimentoBlocoCompletoTable | undefined
    selectedBlocoAlteracao: blocoAlteracao | undefined
    selectedUnidadeAlteracao: unidadeAlteracao | undefined
    setSelectedBlocoAlteracao: (val: blocoAlteracao) => void
    setSelectedUnidadeAlteracao: (val: unidadeAlteracao) => void
    onDoubleClickBloco: (idBloco: number, descricao: string | null) => void
    setListBlocoCompleteTable: (val: ListaEmpreendimentoBlocoCompletoTable) => void
    onBlocoTitleChange: (newName: string, idBloco: number) => void
    onUnidadeTitleChange: (newName: string, idUnidade: number, idBloco: number) => void
    onDoubleClickUnidade: (idUnidade: number, descricao: string | null) => void
    onOpenDeleteModal: (idBloco: number | null, openState: boolean) => void
    onOpenCopyModal: (idBloco: number | null, openState: boolean) => void
    onOpenUnidadesModal: (idBloco: number | null, openState: boolean) => void
    onOpenDeleteUnidadeModal: (idUnidade: number | null, descricao: string | null, openState: boolean) => void

}

const UNIDADES_POR_LINHA_CASA = 10

const computeBlocoNome = (bloco: blocoAlteracao | undefined, blocoTable: EmpreendimentoBlocoCompletoTable | undefined) => {
    if (bloco?.idBloco === blocoTable?.listIncorporacaoImovel[0]?.idIncorporacaoBloco) {
        return bloco?.descricao ?? 'Bloco'
    } else {
        return blocoTable?.nomeBloco ?? 'Bloco'
    }
};

const computeUnidadeNome = (unidade: unidadeAlteracao | undefined, imovel: IncorporacaoImovel | undefined) => {
    if (unidade?.idUnidade === imovel?.idIncorporacaoImovel) {
        return unidade?.descricao ?? 'Und'
    } else {
        return imovel?.descricao ?? 'Und'
    }
};

export const IncorporacaoBlocoTableEditable = ({
    blocoTable,
    selectedBlocoAlteracao,
    selectedUnidadeAlteracao,
    onDoubleClickBloco,
    onDoubleClickUnidade,
    setSelectedBlocoAlteracao,
    setSelectedUnidadeAlteracao,
    onBlocoTitleChange,
    onUnidadeTitleChange,
    onOpenDeleteModal,
    onOpenCopyModal,
    onOpenUnidadesModal,
    onOpenDeleteUnidadeModal,

}: Props) => {

    // Menu (☰) da unidade: elemento âncora do balão e unidade a que ele se refere.
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null)
    const [menuImovel, setMenuImovel] = useState<IncorporacaoImovel | null>(null)
    const [editRequest, setEditRequest] = useState<{ idUnidade: number, signal: number }>({ idUnidade: 0, signal: 0 })

    const closeMenu = () => {
        setMenuAnchor(null)
        setMenuImovel(null)
    }

    const isApartamento = (blocoTable?.listIncorporacaoAndar.length ?? 0) > 0

    // Apartamentos: um andar por linha, do mais alto para o mais baixo.
    // Casas: linhas de UNIDADES_POR_LINHA_CASA unidades, sem rótulo de andar.
    const linhas = useMemo(() => {
        if (blocoTable === undefined) { return [] }

        if (isApartamento) {
            return [...blocoTable.listIncorporacaoAndar].reverse().map((andar) => ({
                key: `andar-${andar.idIncorporacaoAndar}`,
                label: `${andar.nroAndar}°` as string | null,
                imoveis: blocoTable.listIncorporacaoImovel.filter(y => y.idIncorporacaoAndar === andar.idIncorporacaoAndar)
            }))
        }

        const imoveis = blocoTable.listIncorporacaoImovel
        const result: { key: string, label: string | null, imoveis: IncorporacaoImovel[] }[] = []
        for (let i = 0; i < imoveis.length; i += UNIDADES_POR_LINHA_CASA) {
            result.push({
                key: `linha-${i}`,
                label: null,
                imoveis: imoveis.slice(i, i + UNIDADES_POR_LINHA_CASA)
            })
        }
        return result
    }, [blocoTable, isApartamento])

    if (blocoTable === undefined) { return <></> }

    const idBloco = blocoTable.idIncorporacaoBloco ?? blocoTable.listIncorporacaoImovel[0]?.idIncorporacaoBloco ?? 0
    const totalUnidades = blocoTable.listIncorporacaoImovel.length

    return (
        <div className="flex justify-center items-start p-4">
            <div className="w-fit max-w-full rounded-lg border border-gray-200 bg-white shadow-sm dark:bg-gray-800 dark:border-gray-700">

                <div className="flex items-center justify-between gap-4 px-4 pt-3 pb-2">
                    <div className="text-base font-semibold text-gray-800 dark:text-white">
                        <EditableCellBloco
                            value={computeBlocoNome(selectedBlocoAlteracao, blocoTable)}
                            idBloco={idBloco}
                            onChangeValue={(c) => {
                                setSelectedBlocoAlteracao({
                                    descricao: c,
                                    idBloco: idBloco
                                })
                            }}
                            onBlocoTitleChange={onBlocoTitleChange}
                            onDoubleClickBloco={() => onDoubleClickBloco(idBloco, blocoTable.nomeBloco)}
                        />
                    </div>
                    <div className="flex items-center gap-1">
                        <Tooltip title={isApartamento ? "Novos andares/unidades" : "Novas unidades"}>
                            <IconButton size="small" onClick={() => onOpenUnidadesModal(idBloco, true)}>
                                <DynamicIcon nameIcon="AddBoxRounded" size={20} className="text-primary-green" />
                            </IconButton>
                        </Tooltip>
                        <IconButton size="small" onClick={() => onOpenCopyModal(idBloco, true)}>
                            <DynamicIcon nameIcon="ContentCopy" size={20} className="text-purple-500" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onOpenDeleteModal(idBloco, true)}>
                            <DynamicIcon nameIcon="DeleteForeverRounded" size={20} className="text-red-500" />
                        </IconButton>
                    </div>
                </div>

                <div className="mx-4 h-1 rounded-full bg-primary-green" />

                <div className="overflow-x-auto px-4 py-4">
                    <div className="flex flex-col gap-2">
                        {linhas.map((linha) => (
                            <div key={linha.key} className="flex items-center gap-2">
                                {isApartamento &&
                                    <div className="w-8 shrink-0 text-xs text-gray-500 dark:text-gray-400">
                                        {linha.label}
                                    </div>
                                }
                                {linha.imoveis.map((y) => (
                                    <div
                                        key={y.idIncorporacaoImovel}
                                        className="flex min-h-[2.75rem] min-w-[4.5rem] items-center justify-center gap-1 rounded-md bg-gray-200 pl-2 pr-0.5 text-center text-sm font-semibold text-gray-700 cursor-pointer transition-colors hover:bg-gray-300 dark:bg-gray-600 dark:text-white dark:hover:bg-gray-500"
                                    >
                                        <EditableCellUnidade
                                            value={computeUnidadeNome(selectedUnidadeAlteracao, y)}
                                            idBloco={y.idIncorporacaoBloco ?? 0}
                                            idUnidade={y.idIncorporacaoImovel ?? 0}
                                            onChangeValue={(c) => {
                                                setSelectedUnidadeAlteracao({
                                                    idUnidade: y.idIncorporacaoImovel,
                                                    descricao: c
                                                })
                                            }}
                                            onUnidadeTitleChange={onUnidadeTitleChange}
                                            onDoubleClickUnidade={() => onDoubleClickUnidade(y.idIncorporacaoImovel ?? 0, y.descricao ?? '')}
                                            editSignal={editRequest.idUnidade === y.idIncorporacaoImovel ? editRequest.signal : 0}
                                        />
                                        <IconButton
                                            size="small"
                                            sx={{ padding: '2px' }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                setMenuAnchor(e.currentTarget)
                                                setMenuImovel(y)
                                            }}
                                            onDoubleClick={(e) => e.stopPropagation()}>
                                            <DynamicIcon nameIcon="MenuRounded" size={16} className="text-gray-500 dark:text-gray-300" />
                                        </IconButton>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>

                <Menu
                    anchorEl={menuAnchor}
                    open={menuAnchor !== null}
                    onClose={closeMenu}
                    // Sem devolver o foco ao botão (☰), senão o input de edição perde o foco e fecha.
                    disableRestoreFocus
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                    <MenuItem
                        onClick={() => {
                            if (menuImovel) {
                                setEditRequest(prev => ({ idUnidade: menuImovel.idIncorporacaoImovel, signal: prev.signal + 1 }))
                            }
                            closeMenu()
                        }}>
                        <ListItemIcon>
                            <DynamicIcon nameIcon="EditRounded" size={18} className="text-primary-green" />
                        </ListItemIcon>
                        <ListItemText>Editar</ListItemText>
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            if (menuImovel) {
                                onOpenDeleteUnidadeModal(menuImovel.idIncorporacaoImovel, menuImovel.descricao ?? null, true)
                            }
                            closeMenu()
                        }}>
                        <ListItemIcon>
                            <DynamicIcon nameIcon="DeleteForeverRounded" size={18} className="text-red-500" />
                        </ListItemIcon>
                        <ListItemText>Deletar</ListItemText>
                    </MenuItem>
                </Menu>

                <div className="px-4 pb-3 text-xs text-gray-500 dark:text-gray-400">
                    {totalUnidades} {isApartamento ? 'apartamentos' : 'casas'}
                    {isApartamento && ` · ${blocoTable.listIncorporacaoAndar.length} andares`}
                </div>
            </div>
        </div>
    )
}
